import { BRAND, SITE_ORIGIN } from "../brand";
import { marketingLinks, MarketingShell, socialImageMeta } from "../components/marketing/layout";

export const links = marketingLinks;

export const meta = () => [
  { title: `Support & FAQ — AI B2B Quote Desk for Shopify` },
  { name: "description", content: `Get support for ${BRAND.name}, a B2B quote/PO/AI counter-offer Shopify app. Email response within 24 business hours.` },
  { name: "robots", content: "index,follow" },
  { tagName: "link", rel: "canonical", href: `${SITE_ORIGIN}/support` },
  { property: "og:type", content: "website" },
  { property: "og:url", content: `${SITE_ORIGIN}/support` },
  ...socialImageMeta(),
];

export default function Support() {
  return (
    <MarketingShell>
      <section className="qd-section qd-legal">
        <p className="qd-eyebrow">Support</p>
        <h1 style={{ marginTop: "0.8rem" }}>Support — {BRAND.name}</h1>
        <p>
          Email us at <a href={`mailto:${BRAND.supportEmail}`}>{BRAND.supportEmail}</a>. We aim to respond within 24 business
          hours.
        </p>

        <h2>FAQ</h2>
        <details>
          <summary>Do I need to be on Shopify Plus?</summary>
          <p>No. {BRAND.name} is built for Shopify Basic, Grow, and Advanced merchants. We use Shopify's native sub-Plus B2B
          feature set (company accounts, 3 catalogs, payment terms) and extend it with quote workflow, negotiated per-quote
          pricing, PO PDF parsing, and AI counter-offers.</p>
        </details>

        <details>
          <summary>How does the buyer view a quote?</summary>
          <p>When you send a quote, the buyer receives an email with a secure link. Clicking the link takes them to a hosted
          quote page where they can review line items and accept or decline. No Shopify customer account is required.</p>
        </details>

        <details>
          <summary>What PO PDF formats are supported?</summary>
          <p>Any PDF — including scanned or photographed POs. Our parser uses a vision-LLM and gracefully handles weird
          formats. Lines below 0.7 confidence are flagged for manual review before draft order creation.</p>
        </details>

        <details>
          <summary>How does the AI counter-offer work?</summary>
          <p>When you have a quote in progress, you can request a counter-offer draft. We pull the company's last 12 months of
          order history (line items, quantities, prices, dates), compress it into a per-variant summary, and feed it to an LLM
          with the current quote. The LLM proposes adjusted unit prices with a per-line rationale. You can edit every line
          before sending.</p>
        </details>

        <details>
          <summary>What's the verification harness?</summary>
          <p>It replays your current pricing rules against historical orders for a company and reports how many would have
          produced identical totals. Useful when you change your pricing rules or migrate from another wholesale-pricing app
          and want to prove there's no regression before applying it to new quotes.</p>
        </details>

        <details>
          <summary>Can I cancel anytime?</summary>
          <p>Yes. Cancel from within Shopify or from the app's billing settings. Shopify pro-rates the remaining time on your
          current period.</p>
        </details>

        <details>
          <summary>How does pricing work between tiers?</summary>
          <p>The Free plan runs the full quote workflow — company directory, public quote links, negotiated per-quote
          pricing, and branded PDFs — for up to 5 quotes every 30 days, free forever. Starter at $29/mo removes the cap
          (unlimited quotes) and adds CSV bulk import and filtered export. Pro at $49/mo adds PO PDF parsing — drop a
          buyer's PO and we parse it into a draft order with confidence-flagged line items. Growth at $79/mo adds the AI
          counter-offer engine (drafted from order history) and the pricing-rule verification harness. Every paid tier
          comes with a 14-day free trial; you can upgrade or downgrade at any time, and Shopify pro-rates the change. If
          you cancel or a trial lapses, you drop back to the Free plan rather than losing access. There's no annual
          contract — month-to-month only.</p>
        </details>

        <details>
          <summary>What data do you store about my buyers?</summary>
          <p>The minimum needed to run the quote workflow: buyer email, buyer name (optional), and any notes you or the buyer
          attach to a quote. We do not collect IP addresses, browser fingerprints, or passive tracking data. When you trigger
          an AI counter-offer, we pull the buyer's order history (line items, quantities, prices, dates) and compress it into
          a per-variant summary that goes to our LLM provider. The full order rows are not retained outside Shopify; we keep
          the compressed corpus and the resulting AI suggestion. On a customer redact webhook, we anonymize the buyer email
          and name on every quote referencing that customer.</p>
        </details>

        <details>
          <summary>Where is my data stored?</summary>
          <p>Application and database hosting: Railway (US-East). Background jobs (PO parse, AI counter-offer, verification):
          Trigger.dev. LLM inference: OpenRouter (model varies — we A/B between providers for cost and quality). Email
          delivery (optional): Resend. Error monitoring (optional): Sentry. None of these subprocessors retain your data
          beyond the operational lifetime of the request. See our <a href="/privacy">privacy policy</a> for the full list of
          subprocessors and data retention policies.</p>
        </details>

        <details>
          <summary>How do I export my quote history?</summary>
          <p>For now, contact <a href={`mailto:${BRAND.supportEmail}`}>{BRAND.supportEmail}</a> with your shop domain and we'll
          deliver a CSV export within 5 business days. A self-serve export tool is on our post-launch roadmap. If you uninstall
          the app, we retain your data for 30 days for reinstall continuity, then hard-delete every record in line with our
          privacy policy.</p>
        </details>

        <details>
          <summary>What if my buyer doesn't have a Shopify customer account?</summary>
          <p>That's the default case for most B2B buyers and Quote Desk is built around it. Every quote you send includes a
          signed link to a hosted quote page on our domain. The buyer clicks Accept or Decline directly — no Shopify login,
          no customer account creation. The signed link is unique to that quote and can be regenerated if it's ever leaked or
          forwarded.</p>
        </details>

        <details>
          <summary>Can I customize the buyer email?</summary>
          <p>The default email is plain and merchant-anchored. Custom branding (logo, colors, sender name) is on the post-launch
          roadmap — email <a href={`mailto:${BRAND.supportEmail}`}>{BRAND.supportEmail}</a> if this is blocking you and we'll
          prioritize it. In the meantime, you can also forward your own quote PDF to buyers and use the buyer link only as a
          secondary accept channel.</p>
        </details>
      </section>
    </MarketingShell>
  );
}
